import { useContext } from 'react';
import { Select, Space, Table as TableAntd, Tooltip } from 'antd';
import TableCard from '../../../../../components/TableCard';
import TableActionButton from '../../../../../components/TableActionButton';
import { IGiveawayWithDetails } from '../../../../../interfaces';
import { poeValorComPontosEmMil } from "../../../../../utils/Conversores";
import GiveawayStatusTag from '../../../../Giveaways/components/GiveawayStatusTag';
import { ThemeContext } from '../../../../../contexts/ThemeContext';

function renderDateTimeCell(data: any, isDark: boolean) {
    if (!data) return <span style={{ color: isDark ? '#666' : '#94a3b8' }}>--</span>;

    const d = new Date(data);
    if (isNaN(d.getTime())) return <span style={{ color: isDark ? '#666' : '#94a3b8' }}>--</span>;

    const dateStr = d.toLocaleDateString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });

    const timeStr = d.toLocaleTimeString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        hour: '2-digit',
        minute: '2-digit',
    });

    return (
        <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.25 }}>
            <span style={{ color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>
                {dateStr}
            </span>
            <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 11, fontWeight: 400, whiteSpace: 'nowrap', marginTop: 1 }}>
                {timeStr}
            </span>
        </div>
    );
}

interface IProps {
    handleSetSelectedGiveaway: (giveaway: IGiveawayWithDetails) => void;
    handleShowModalTickets: () => void;
    filterGiveaways: 'all' | 'withTickets';
    filteredGiveaways: IGiveawayWithDetails[];
    handleSetFilterGiveaway: (filter: "all" | "withTickets") => void;
}

export default function TabGiveaways({
    handleSetFilterGiveaway,
    filteredGiveaways,
    filterGiveaways,
    handleSetSelectedGiveaway,
    handleShowModalTickets,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const columns = [
        {
            title: 'Sorteio',
            dataIndex: 'description',
            key: 'description',
            width: 190,
            sorter: (a: IGiveawayWithDetails, b: IGiveawayWithDetails) => a.description.localeCompare(b.description),
            render: (desc: string) => (
                <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 13 }}>
                    {desc}
                </span>
            ),
        },
        {
            title: 'Início',
            dataIndex: 'period_beginning',
            key: 'period_beginning',
            align: 'center' as const,
            width: 105,
            sorter: (a: IGiveawayWithDetails, b: IGiveawayWithDetails) =>
                new Date(a.period_beginning).getTime() - new Date(b.period_beginning).getTime(),
            render: (data: any) => renderDateTimeCell(data, isDark),
        },
        {
            title: 'Fim',
            dataIndex: 'period_ending',
            key: 'period_ending',
            align: 'center' as const,
            width: 105,
            sorter: (a: IGiveawayWithDetails, b: IGiveawayWithDetails) =>
                new Date(a.period_ending).getTime() - new Date(b.period_ending).getTime(),
            render: (data: any) => renderDateTimeCell(data, isDark),
        },
        {
            title: 'Data do Sorteio',
            dataIndex: 'date',
            key: 'date',
            align: 'center' as const,
            width: 160,
            sorter: (a: IGiveawayWithDetails, b: IGiveawayWithDetails) => 
                (a.date ? new Date(a.date).getTime() : 0) - (b.date ? new Date(b.date).getTime() : 0),
            render: (data: any) => renderDateTimeCell(data, isDark),
        },
        {
            title: 'Limite Importação',
            dataIndex: 'import_deadline',
            key: 'import_deadline',
            align: 'center' as const,
            width: 165,
            sorter: (a: IGiveawayWithDetails, b: IGiveawayWithDetails) => 
                new Date(a.import_deadline).getTime() - new Date(b.import_deadline).getTime(),
            render: (data: any) => renderDateTimeCell(data, isDark),
        },
        {
            title: 'Status',
            key: 'status',
            align: 'center' as const,
            width: 130,
            render: (_: any, record: IGiveawayWithDetails) => (
                <GiveawayStatusTag
                    status={record.status}
                    isValid={record.is_valid}
                    key={`tag-${record.id}`}
                />
            ),
        },
        {
            title: 'Bilhetes',
            dataIndex: 'total_tickets',
            key: 'total_tickets',
            align: 'center' as const,
            width: 110,
            sorter: (a: IGiveawayWithDetails, b: IGiveawayWithDetails) => (a.total_tickets || 0) - (b.total_tickets || 0),
            render: (tickets?: number) => (
                <Tooltip color="#722ed1" title="Bilhetes do usuário neste sorteio">
                    <span style={{ fontWeight: 600, color: '#722ed1', fontSize: 13.5, whiteSpace: 'nowrap' }}>
                        {poeValorComPontosEmMil(tickets ?? 0)}
                    </span>
                </Tooltip>
            ),
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 80,
            fixed: 'right' as const,
            render: (_: any, record: IGiveawayWithDetails) => (
                (record.total_tickets || 0) > 0 ? (
                    <TableActionButton
                        action="view"
                        tooltip="Visualizar bilhetes do sorteio"
                        onClick={() => {
                            handleSetSelectedGiveaway(record);
                            handleShowModalTickets();
                        }}
                    />
                ) : (
                    <span style={{ color: isDark ? '#666' : '#94a3b8' }}>--</span>
                )
            ),
        },
    ];

    return (
        <div style={{ paddingTop: 4 }}>
            {/* Filter Bar */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 12,
                    marginBottom: 12,
                }}
            >
                <Space align="center">
                    <span style={{ fontSize: 13, color: isDark ? '#8c8c8c' : '#64748b' }}>Filtrar:</span>
                    <Select
                        value={filterGiveaways}
                        onChange={handleSetFilterGiveaway}
                        style={{ minWidth: 200 }}
                        size="middle"
                    >
                        <Select.Option value="all">Todos os sorteios</Select.Option>
                        <Select.Option value="withTickets">Apenas com bilhetes</Select.Option>
                    </Select>
                </Space>

                <span style={{ fontSize: 12.5, color: isDark ? '#8c8c8c' : '#64748b' }}>
                    {filteredGiveaways.length} {filteredGiveaways.length === 1 ? 'sorteio' : 'sorteios'}
                </span>
            </div>

            {/* Giveaways Table */}
            <TableCard>
                <TableAntd
                    size="middle"
                    className={`modern-data-table ${isDark ? 'modern-data-table-dark' : ''}`}
                    columns={columns}
                    dataSource={filteredGiveaways.map((g) => ({
                        ...g,
                        key: g.id,
                    }))}
                    scroll={{ x: 'max-content' }}
                    onRow={(record) => ({
                        onClick: () => {
                            if ((record.total_tickets || 0) > 0) {
                                handleSetSelectedGiveaway(record);
                                handleShowModalTickets();
                            }
                        },
                        style: {
                            cursor: (record.total_tickets || 0) > 0 ? 'pointer' : 'default',
                        },
                    })}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                    }}
                />
            </TableCard>
        </div>
    );
}



